import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section,
} from "../components/Primitives";
import { Ornament } from "../components/Ornament";
import { CrisisPanel } from "./Booking";
import { getConfig, postContact, apiError } from "../lib/api";
import { useSEO } from "../lib/seo";
import { COMPANY } from "../content/nav";
import { IMAGES } from "../content/images";

export function Puja() {
  useSEO({
    title: "Puja & Anushthan — Named Priest, Named Place, Video Record",
    description:
      "Traditional rites arranged with a named priest at a named place on a stated date, with a video record. Never sold on a call, and no consultant earns anything from one.",
    canonical: "/consult/puja",
  });
  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Consult", to: "/consult" }, { label: "Puja & Anushthan" }]} />
      </Container>
      <Section>
        <Container className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Eyebrow>Priced per rite · never sold on a call</Eyebrow>
            <H1 className="mt-5">Puja &amp; Anushthan</H1>
            <Lede className="mt-6">
              We arrange traditional rites with a named priest at a named place, on a stated date, and send
              you the video with the date visible.
            </Lede>
            <div className="measure mt-8 space-y-5 text-base text-dim">
              <p>
                <strong className="text-ink">What you get.</strong> The priest's name and lineage, the
                place, the date and time, the sankalpa as recited, the number of repetitions, and a video
                of the rite with the date visible. Prasad is posted where the rite produces any.
              </p>
              <p>
                <strong className="text-ink">What we will never do.</strong> We will never sell you a puja
                during a consultation. No consultant earns anything from a puja, and none of them can
                book one for you. If anyone representing us offers you a puja as a remedy on a call, that
                is a breach of their contract — report it to {COMPANY.grievance_email} and the session is
                refunded in full.
              </p>
              <p>
                <strong className="text-ink">No claims.</strong> A puja is a traditional ritual practice.
                We make no claim about outcome, and we will not tell you that skipping one carries a
                consequence. Any page that does is selling fear.
              </p>
              <p>
                <strong className="text-ink">How to enquire.</strong> Write to us with the rite, the reason
                and the timeframe. We will quote a single price, inclusive, with the priest and place
                named before you pay anything.
              </p>
            </div>
            <Link to="/contact" className="btn btn-primary mt-8" data-testid="puja-enquire">
              Enquire about a rite
            </Link>
          </div>
          <div>
            <img src={IMAGES.yantra} alt="A hand-engraved copper yantra plate photographed flat on a neutral surface" className="w-full border border-rule" />
          </div>
        </Container>
      </Section>
    </>
  );
}

export function ScopeOfPractice() {
  useSEO({
    title: "Scope of Practice",
    description:
      "What we provide, what we do not provide, and why we deliberately avoid the words counselling and therapy. A tick-box acceptance is required at booking.",
    canonical: "/consult/scope-of-practice",
  });
  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Consult", to: "/consult" }, { label: "Scope of practice" }]} />
      </Container>
      <Section>
        <Container className="max-w-3xl">
          <Eyebrow>Read this before your first consultation</Eyebrow>
          <H1 className="mt-4">Scope of Practice</H1>
          <div className="measure mt-8 space-y-6 text-sm leading-relaxed text-dim">
            <div>
              <H2 className="h2-sub">What we provide</H2>
              <p className="mt-2">
                Interpretive guidance drawn from traditional Vedic astrology, numerology and allied
                traditional disciplines, offered as a framework for personal reflection and
                decision-making. Our consultants are trained in these traditional disciplines and their
                training is stated on their profile pages.
              </p>
            </div>
            <div>
              <H2 className="h2-sub">What we do not provide</H2>
              <p className="mt-2">
                We do not provide any medical, psychiatric, psychological, clinical or therapeutic service.
                We do not diagnose, assess, treat or manage any illness, disorder or condition, whether
                physical or mental. We do not provide crisis intervention or emergency mental health care.
                We do not provide legal, financial, tax or investment advice.
              </p>
            </div>
            <div>
              <H2 className="h2-sub">What our consultants are not</H2>
              <p className="mt-2">
                Unless a specific registration number is displayed on their profile, our consultants are
                not registered medical practitioners, psychiatrists, RCI-registered clinical psychologists,
                or registered allied and healthcare professionals.
              </p>
            </div>
            <div>
              <H2 className="h2-sub">On medical treatment and predictions</H2>
              <p className="mt-2">
                We will not advise you to stop, delay, reduce or replace any medical treatment, medication
                or professional care prescribed to you. If any consultant does so, that is contrary to our
                policy — report it immediately to {COMPANY.grievance_email}. We will not make predictions
                or claims about your health, your survival, the timing of any person's death, pregnancy
                outcomes, or the outcome of any pending medical treatment. No outcome is guaranteed.
                Decisions and their consequences remain entirely yours.
              </p>
            </div>
            <div>
              <H2 className="h2-sub">A note on language</H2>
              <p className="mt-2">
                You will see other businesses in this field describe what they do as "counselling" or
                "therapy". We deliberately do not, because those words carry specific professional meanings
                in India and we are not making that claim. What we offer is a guidance session in a
                traditional discipline. We think being precise about that is more respectful of both
                traditions.
              </p>
            </div>
          </div>
          <div className="mt-10"><CrisisPanel /></div>
        </Container>
      </Section>
    </>
  );
}

export function Crisis() {
  useSEO({
    title: "If you are in crisis",
    description:
      "Crisis helpline numbers, and exactly what our consultants are required to do if you disclose acute distress during a session.",
    canonical: "/consult/if-you-are-in-crisis",
  });
  return (
    <Section>
      <Container className="max-w-3xl">
        <H1>If you are in crisis</H1>
        <p className="ui mt-5 text-base font-semibold text-ink">
          If you are having thoughts of harming yourself or someone else, or you are in immediate danger,
          please do not use this service. Contact one of the following now.
        </p>
        <div className="mt-6"><CrisisPanel /></div>
        <div className="measure mt-10 space-y-6 text-sm leading-relaxed text-dim">
          <div>
            <H2 className="h2-sub">What our consultants are required to do</H2>
            <p className="mt-2">
              If you disclose thoughts of self-harm, abuse, or acute distress during a session, our
              consultants are required to: stop the reading immediately; not interpret what you have said
              astrologically; not offer a remedy; give you the numbers above and stay with you until you
              confirm you have them; refund the session in full; and log the incident so we can check we
              handled it properly.
            </p>
          </div>
          <div>
            <H2 className="h2-sub">What they are forbidden from doing</H2>
            <p className="mt-2">
              They are specifically forbidden from telling you that a gemstone, a rudraksha or a puja will
              resolve a mental health crisis, and from advising you to stop taking prescribed medication.
            </p>
          </div>
          <p>
            We publish this policy because we would rather you knew what we will do before you need us to
            do it.
          </p>
        </div>
      </Container>
    </Section>
  );
}

export function Guides() {
  const { data } = useQuery({ queryKey: ["config"], queryFn: getConfig });
  const guides = data?.guides || [];
  const sections = [...new Set(guides.map((g) => g.section))];

  useSEO({
    title: "Guides — Prices, Certification and Honest Comparisons",
    description:
      "Price-per-carat guides for all nine navratna, how to read a laboratory report line by line, the documented certificate-substitution cases, and Nepal versus Java rudraksha.",
    canonical: "/guide",
  });

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Guides" }]} />
      </Container>
      <Section>
        <Container>
          <Eyebrow>Knowledge hub</Eyebrow>
          <H1 className="mt-5 max-w-3xl">Guides</H1>
          <Lede className="mt-6">
            Written as though we were not the seller. Where the honest answer is that a cheaper option
            would serve you better, the guide says so.
          </Lede>

          {sections.map((sec) => (
            <div key={sec} className="mt-14">
              <H2>{sec}</H2>
              <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {guides.filter((g) => g.section === sec).map((g) => (
                  <article key={g.slug} className="card-s p-6" data-testid={`guide-${g.slug.replace(/\//g, "-")}`}>
                    <p className="ui text-[11px] uppercase tracking-wider text-gold">Tier {g.tier}</p>
                    <H3 className="mt-2">{g.title}</H3>
                    <p className="ui mt-2 text-sm leading-relaxed text-dim">{g.summary}</p>
                    <p className="ui mt-4 text-xs italic text-dim">
                      Full article in preparation — this hub, the festival templates and the per-stone
                      guides are the Tier 1 and Tier 2 content plan.
                    </p>
                  </article>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-16 border-t border-rule pt-8" data-testid="guides-shop-cta-block">
            <p className="ui text-sm text-dim">
              Guides tell you how to check a stone. The inventory is where you check us.
            </p>
            <Link to="/shop/gemstones" className="btn btn-primary mt-4" data-testid="guides-shop-cta">
              See the certified stones we have right now
            </Link>
          </div>

          <div className="card-s mt-16 p-8">
            <H2>Festival and muhurat content</H2>
            <p className="measure ui mt-3 text-sm leading-relaxed text-dim">
              Festival and calendar pages carry more organic traffic in this category than product pages
              do. Each entry gets the dates, the muhurat timings, what is traditionally done, and — attached
              at the bottom, not shouted at the top — the relevant gifting collection.
            </p>
            <Link to="/tools/muhurat-calendar" className="btn btn-secondary mt-5">
              Open the muhurat calendar
            </Link>
          </div>
        </Container>
      </Section>
    </>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [ticket, setTicket] = useState(null);
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Contact — A Named Human and a Real Phone Number",
    description:
      "Acknowledged within 24 hours with a ticket number, resolved within 15 days, no templated first replies. Escalation ends at the founder's inbox.",
    canonical: "/contact",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await postContact(form);
      setTicket(r);
      toast.success(r.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section>
      <Container className="grid gap-12 lg:grid-cols-2">
        <div>
          <Eyebrow>No templated first replies</Eyebrow>
          <H1 className="mt-4">Contact</H1>
          <Lede className="mt-5">
            We acknowledge every message within 24 hours with a ticket number and resolve within 15 days.
            Your first reply comes from a named person.
          </Lede>
          <dl className="mt-8 space-y-4 text-sm">
            {[
              ["Customer care", COMPANY.phone ? `${COMPANY.email} · ${COMPANY.phone}` : COMPANY.email],
              ["Hours", COMPANY.hours],
              ["Grievance Officer", `${COMPANY.grievance_name}, ${COMPANY.grievance_designation} · ${COMPANY.grievance_email}`],
              ["Privacy and data", COMPANY.privacy_email],
              ["Registered office", COMPANY.address],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="ui text-xs uppercase tracking-wider text-dim">{k}</dt>
                <dd className="num mt-1 text-ink">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="ui mt-8 text-xs text-dim">
            We will never call you unless you book a call. We do not make outbound marketing calls, ever.
          </p>
        </div>

        <div>
          {ticket ? (
            <div className="card-s p-6" data-testid="contact-ticket">
              <H3>Ticket <span className="num">{ticket.ticket}</span></H3>
              <p className="ui mt-2 text-sm text-dim">{ticket.message}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="card-s space-y-4 p-6" data-testid="contact-form">
              <label className="block">
                <span className="field-label">Your name</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" data-testid="contact-name" />
              </label>
              <label className="block">
                <span className="field-label">Email</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" data-testid="contact-email" />
              </label>
              <label className="block">
                <span className="field-label">Subject</span>
                <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="field" data-testid="contact-subject" />
              </label>
              <label className="block">
                <span className="field-label">Message</span>
                <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="field" data-testid="contact-message" />
              </label>
              <button type="submit" disabled={busy} className="btn btn-primary" data-testid="contact-submit">
                {busy ? "Sending…" : "Send"}
              </button>
            </form>
          )}
        </div>
      </Container>
    </Section>
  );
}

const FAQS = [
  ["Do you sell anything without a laboratory report?", "Yes, and we say so plainly on the page. A ₹1,200 rudraksha bead does not justify the cost of a report, and any laboratory that would issue one cheaply is a laboratory we refuse. Those items carry a self-described badge that links to exactly what we can and cannot tell you."],
  ["Why is the first consultation not free?", "Because free advice trains everyone involved to treat the advice as worthless. ₹499 is small enough not to matter and large enough to mean you are serious — and it is credited in full against any purchase over ₹5,000 within thirty days."],
  ["What happens if I want a refund on a consultation I already had?", "Once delivered as booked, a consultation is not refundable. But if you tell us within seven days that it was not worth what you paid, the founder reads your message personally and decides — and we publish the proportion of such requests we grant."],
  ["Do you have a wallet or store credit?", "No. No wallet, no stored balance, no bonus credit, and therefore nothing to deduct from a refund. Refunds go back to the card or account you paid with."],
  ["Can I negotiate the price?", "No. One honest price is the point, and a discount for whoever asks is a penalty on whoever doesn't. If you think the value isn't there, tell us what's missing — we would rather add to what's included."],
  ["Do you ship internationally, and will there be duty on arrival?", "Free insured shipping worldwide, Delivered Duty Paid wherever the carrier supports it, so there is nothing to pay on delivery. Where a lane does not support DDP we say so before payment and show the estimated duty."],
  ["Will you tell me not to buy something?", "Regularly. The percentage of consultations that end with no product recommendation is published on our scoreboard and updated weekly."],
  ["Are my birth details safe?", "They are used only to prepare your chart, never sold, never shared with a second consultant without your explicit request, never used for advertising or model training, and encrypted at rest. One click deletes everything and we email you a confirmation."],
  ["Do you provide counselling or therapy?", "No, and we deliberately do not use those words. Those terms carry specific professional meanings in India and we are not making that claim. What we offer is a guidance session in a traditional discipline."],
  ["What if I'm in distress during a session?", "The consultant is required to stop, not interpret it astrologically, not offer a remedy, give you the crisis helpline numbers, refund the session in full and log the incident."],
];

export function Faq() {
  useSEO({
    title: "Frequently Asked Questions",
    description:
      "Straight answers on certification, pricing, refunds, wallets, international duty, birth-data privacy and what we will not claim.",
    canonical: "/faq",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({
        "@type": "Question", name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  });
  return (
    <Section>
      <Container className="max-w-3xl">
        <Eyebrow>Straight answers</Eyebrow>
        <H1 className="mt-4">Frequently asked questions</H1>
        <dl className="mt-10 divide-y divide-rule" data-testid="faq-list">
          {FAQS.map(([q, a]) => (
            <div key={q} className="py-6">
              <dt><H3>{q}</H3></dt>
              <dd className="ui mt-2 text-sm leading-relaxed text-dim">{a}</dd>
            </div>
          ))}
        </dl>
      </Container>
    </Section>
  );
}

export function NotFound() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  useSEO({ title: "Page not found", noindex: true });
  return (
    <Section className="!py-[110px] max-md:!py-[70px]">
      <Container className="max-w-2xl text-center">
        <Ornament className="mb-6" />
        <Eyebrow>404</Eyebrow>
        <H1 className="mt-4">
          This page doesn't exist — but every stone we've ever sold still has its page
        </H1>
        <Lede className="mx-auto mt-5">
          We never delete a product URL. If you followed a link to a stone, check the spelling or try
          the sold archive — its report is still verifiable on the laboratory's own website.
        </Lede>
        <form
          className="mx-auto mt-8 flex max-w-md gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim()) navigate(`/shop/all?q=${encodeURIComponent(q.trim())}`);
          }}
          data-testid="notfound-search-form"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search stones, mukhi, report number"
            className="field"
            aria-label="Search"
            data-testid="notfound-search"
          />
          <button type="submit" className="btn btn-primary shrink-0">Search</button>
        </form>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/shop" className="btn btn-secondary" data-testid="notfound-shop">Back to the shop</Link>
          <Link to="/shop/sold-archive" className="btn btn-secondary" data-testid="notfound-archive">Sold archive</Link>
          <Link to="/promises" className="btn btn-secondary" data-testid="notfound-promises">The Twelve Promises</Link>
        </div>
      </Container>
    </Section>
  );
}
